export const categories = [
  { slug: 'rules', name: '憋尿任务挑战规则', icon: 'ri-file-list-3-line', description: '了解憋尿任务的基本规则、评分标准和挑战流程，确保挑战公平有序进行。' },
  { slug: 'precautions', name: '憋尿任务注意事项', icon: 'ri-error-warning-line', description: '挑战前必须了解的注意事项，包括环境准备、身体状态评估和紧急应对措施。' },
  { slug: 'health-risks', name: '憋尿任务健康风险', icon: 'ri-heart-pulse-line', description: '深入解析憋尿对泌尿系统、肾脏和膀胱的潜在健康风险，切勿忽视身体信号。' },
  { slug: 'scenarios', name: '憋尿任务场景设定', icon: 'ri-landscape-line', description: '不同场景下的憋尿任务设定方案，从日常训练到极限挑战的场景搭建指南。' },
  { slug: 'techniques', name: '憋尿挑战技巧', icon: 'ri-lightbulb-line', description: '科学有效的憋尿技巧分享，帮助你在安全范围内提升憋尿能力和控制力。' },
  { slug: 'bladder-health', name: '膀胱健康知识', icon: 'ri-mental-health-line', description: '膀胱健康的科普知识与日常养护方法，了解膀胱功能、容量与保护要点。' },
];

export const articles = [
  {
    slug: 'basic-rules-guide',
    title: '憋尿任务挑战基础规则详解——从入门到精通',
    category: 'rules',
    excerpt: '憋尿任务挑战有一套完整的规则体系。本文详细介绍了挑战的基本规则、评分标准、时间计算方式以及常见的规则误区，帮助新手快速上手。',
    content: `## 挑战基本规则

憋尿任务挑战是一项需要严格遵守规则的活动。以下是基础规则框架：

### 1. 挑战时间计算
挑战时间从参与者最后一次排尿后开始计时，直至下一次排尿为止。中途不得有任何形式的排尿行为，否则视为挑战失败。

### 2. 饮水量标准
参与者需在规定时间内饮用指定量的水（通常为500ml-1000ml），以保证膀胱有充足的尿液积累。饮水量根据个人体重和健康状况可适当调整。

### 3. 评分体系
挑战评分综合考虑以下因素：
- **持续时间**：憋尿时间越长，基础分越高
- **饮水量**：在安全范围内饮水量越大，加成越多
- **完成度**：是否在预定的结束时间准时排尿
- **状态评估**：挑战过程中的身体状态稳定性

### 4. 安全红线
任何挑战都必须遵守安全第一原则。出现以下情况必须立即终止挑战：
- 剧烈腹痛或腰部疼痛
- 头晕、恶心或出汗异常
- 无法正常站立或行走

### 5. 挑战等级划分
- **初级**：憋尿1-2小时，适用于初学者
- **中级**：憋尿3-4小时，适用于有一定经验的参与者
- **高级**：憋尿5-6小时，需在专业人士指导下进行
- **极限**：超过6小时，仅限特殊训练场景且必须有医疗监护`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20geometric%20composition%20with%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20flowing%20curves%20representing%20rules%20and%20structure%2C%20clean%20modern%20aesthetic%2C%20no%20text&width=800&height=450&seq=img-basic-rules&orientation=landscape',
    readTime: '8',
    publishedAt: '2026-07-15',
  },
  {
    slug: 'advanced-scoring-system',
    title: '憋尿挑战进阶评分机制——多维度的综合评级',
    category: 'rules',
    excerpt: '除了基础评分外，憋尿挑战还有一套进阶评分机制。本文介绍多维度综合评级体系，包括心理素质、身体控制力和环境适应能力的评估方法。',
    content: `## 进阶评分机制

在掌握基础规则后，进阶评分机制能更全面地评估参与者的综合能力。

### 心理素质评分
憋尿不仅是身体上的挑战，更是心理上的考验。心理素质评分考量以下方面：
- 焦虑控制能力
- 注意力集中度
- 情绪稳定性

### 身体控制力评分
身体控制力是憋尿挑战的核心能力指标：
- 盆底肌群力量
- 膀胱括约肌控制
- 整体肌肉协调性

### 环境适应能力
在不同环境下完成挑战的难度差异很大：
- 静态环境（坐着、躺着）相对容易
- 动态环境（行走、运动）难度增加
- 干扰环境（听到水声、寒冷）难度最大

### 综合评级标准
综合以上维度，将参与者分为五个等级：
- S级：各项指标均达到顶级水平
- A级：各方面表现优秀
- B级：整体表现良好，个别维度有待提升
- C级：基本达标，有较大提升空间
- D级：初级阶段，需加强训练`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20illustration%20of%20scoring%20chart%20and%20rating%20system%20in%20warm%20terracotta%20and%20cream%20tones%2C%20clean%20geometric%20shapes%2C%20no%20text&width=800&height=450&seq=img-scoring&orientation=landscape',
    readTime: '6',
    publishedAt: '2026-07-20',
  },
  {
    slug: 'pre-challenge-preparation',
    title: '憋尿任务挑战前的必备准备工作清单',
    category: 'precautions',
    excerpt: '挑战前的准备工作至关重要。从身体状态评估到环境布置，再到应急预案制定，这份清单帮你做好万全准备。',
    content: `## 挑战前准备工作

充分的准备工作是安全完成憋尿任务挑战的前提。

### 一、身体状态评估
挑战前必须确认以下身体状况：
1. 无泌尿系统疾病史
2. 无肾脏相关疾病
3. 近一周内无腹痛、腰痛等不适
4. 精神状态良好，无焦虑或抑郁情绪
5. 充足的睡眠（前晚至少7小时）

### 二、环境准备
1. 选择安静、温暖、舒适的环境
2. 确保卫生间在可达范围内（紧急情况下）
3. 准备计时设备和记录工具
4. 保持环境温度在22-26°C之间

### 三、饮食准备
1. 挑战前2小时完成最后一餐
2. 避免咖啡因、酒精等利尿饮品
3. 准备适量饮用水（根据挑战等级）

### 四、应急预案
1. 提前告知至少一位同伴你的挑战计划
2. 准备紧急联系人的联系方式
3. 了解最近的医疗机构位置
4. 设置挑战的安全中止条件`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20checklist%20and%20preparation%20concept%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20organic%20shapes%2C%20clean%20modern%20aesthetic%2C%20no%20text&width=800&height=450&seq=img-preparation&orientation=landscape',
    readTime: '7',
    publishedAt: '2026-07-25',
  },
  {
    slug: 'safety-red-lines',
    title: '憋尿任务不可逾越的安全红线——生命第一',
    category: 'precautions',
    excerpt: '任何挑战都不能以牺牲健康为代价。本文详细列举憋尿任务中绝对不能触碰的安全底线，务必牢记于心。',
    content: `## 安全红线

安全是所有挑战活动的最高准则。以下红线绝对不能触碰：

### 绝对禁止行为
1. **禁止超过身体极限**：当身体发出明确信号时必须停止
2. **禁止在药物影响下参与**：任何影响神经系统的药物
3. **禁止在生病期间参与**：感冒、发烧、腹泻等任何不适状态
4. **禁止连续多日高强度挑战**：膀胱需要恢复时间

### 必须终止的信号
以下任何症状出现，立即终止挑战并寻求医疗帮助：
- 剧烈腹痛或痉挛
- 血尿
- 无法排尿（挑战后）
- 发热
- 意识模糊

### 特殊人群禁忌
以下人群绝对不应参与憋尿挑战：
- 孕妇及哺乳期女性
- 有泌尿系统手术史者
- 慢性肾病患者
- 糖尿病患者
- 高血压未控制者
- 65岁以上老人及14岁以下青少年`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20safety%20boundary%20and%20warning%20concept%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20flowing%20lines%20representing%20limits%2C%20no%20text&width=800&height=450&seq=img-safety&orientation=landscape',
    readTime: '5',
    publishedAt: '2026-08-01',
  },
  {
    slug: 'urinary-system-health',
    title: '憋尿对泌尿系统的影响——科学解读与警示',
    category: 'health-risks',
    excerpt: '从医学角度分析憋尿对泌尿系统各器官的影响，了解膀胱、尿道和肾脏在憋尿过程中的变化及潜在损伤机制。',
    content: `## 泌尿系统与憋尿

理解泌尿系统的工作原理，是正确认知憋尿风险的基础。

### 膀胱的生理结构
膀胱是一个中空的肌肉器官，正常成年人的膀胱容量约为400-600ml。当尿液积累到约200ml时，会产生初步尿意；到达400ml时，尿意变得强烈。

### 憋尿的短期影响
1. 膀胱壁肌肉持续紧张
2. 膀胱内压力升高
3. 可能引起暂时性排尿困难
4. 下腹部不适感

### 长期风险
1. **膀胱功能损伤**：反复过度憋尿可能导致膀胱肌肉松弛
2. **尿路感染风险增加**：尿液滞留时间过长，细菌繁殖机会增加
3. **膀胱输尿管反流**：严重情况下可能导致尿液反流至肾脏
4. **盆底肌功能障碍**：长期过度使用可能影响盆底肌群

### 医学建议
- 正常情况下每2-4小时排尿一次
- 每次憋尿时间不建议超过6小时
- 挑战后应充分补充水分
- 如有任何不适及时就医`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20medical%20illustration%20of%20human%20urinary%20system%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20organic%20shapes%2C%20clean%20modern%20aesthetic%2C%20no%20text&width=800&height=450&seq=img-urinary&orientation=landscape',
    readTime: '9',
    publishedAt: '2026-07-18',
  },
  {
    slug: 'kidney-protection',
    title: '憋尿任务中的肾脏保护——不可忽视的重要器官',
    category: 'health-risks',
    excerpt: '肾脏是憋尿过程中最容易受到间接影响的器官之一。了解肾脏保护知识，是每个参与者必备的健康素养。',
    content: `## 肾脏保护

肾脏是人体重要的排泄器官，在憋尿挑战中需要格外关注。

### 肾脏的基本功能
肾脏每天过滤约180升血液，产生约1.5升尿液。它负责：
- 排出代谢废物
- 调节水电解质平衡
- 维持酸碱平衡
- 分泌重要激素

### 憋尿对肾脏的潜在影响
1. 膀胱内高压可能逆向传导
2. 长期反复憋尿增加肾盂压力
3. 尿液反流风险导致肾盂肾炎

### 肾脏保护措施
1. 严格控制憋尿时间上限
2. 挑战后充分补水促进排泄
3. 定期进行尿常规检查
4. 保持健康的生活习惯
5. 控制盐分摄入`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20illustration%20of%20kidney%20health%20and%20protection%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20organic%20shapes%2C%20no%20text&width=800&height=450&seq=img-kidney&orientation=landscape',
    readTime: '7',
    publishedAt: '2026-07-28',
  },
  {
    slug: 'home-training-scenario',
    title: '居家憋尿训练场景搭建——舒适与安全并重',
    category: 'scenarios',
    excerpt: '从零开始搭建居家憋尿训练场景，包括空间布置、设备准备、环境调节以及不同难度级别的场景设计方案。',
    content: `## 居家训练场景

对于大多数参与者来说，居家环境是最方便、最安全的训练场所。

### 基础场景配置
1. **空间选择**：选择通风良好、温度适宜的房间
2. **座椅安排**：准备舒适的座椅或沙发，可备靠垫
3. **娱乐设备**：电视、平板电脑或书籍用于分散注意力
4. **计时设备**：清晰的时钟或计时器

### 环境调节要点
1. 温度控制在22-25°C
2. 避免放置会产生水声的物品
3. 穿着宽松舒适的衣物
4. 保持环境安静或播放舒缓音乐

### 难度分级场景
- **入门级**：躺着或半躺，观看轻松节目
- **进阶级**：坐着工作或阅读，少量活动
- **挑战级**：进行轻度家务或简单运动
- **极限级**：模拟户外活动场景

### 安全设施
1. 卫生间门保持开启状态
2. 通道无障碍物
3. 紧急情况下可快速到达卫生间`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20cozy%20home%20interior%20scene%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20comfortable%20atmosphere%2C%20clean%20modern%20aesthetic%2C%20no%20text&width=800&height=450&seq=img-home-scene&orientation=landscape',
    readTime: '6',
    publishedAt: '2026-08-02',
  },
  {
    slug: 'outdoor-challenge-scenarios',
    title: '户外憋尿挑战场景设计——从公园到长途旅行',
    category: 'scenarios',
    excerpt: '户外场景为憋尿挑战增加了更多变量和难度。本文介绍不同户外场景的挑战方案设计，包括路线规划、时间估算和应急方案。',
    content: `## 户外挑战场景

户外憋尿挑战难度更高，但也更具趣味性和挑战价值。

### 公园/景区场景
1. 选择有明确卫生间分布图的场地
2. 规划1-2小时的步行路线
3. 携带足量饮用水
4. 标记沿途卫生间位置

### 长途交通场景
1. 大巴旅行挑战：适合2-3小时车程
2. 火车旅行挑战：适合3-5小时旅程
3. 自驾游挑战：可在服务区设置检查点

### 户外场景注意事项
1. 天气因素：避免极端高温或低温天气
2. 服装选择：穿着方便快速穿脱的衣物
3. 同伴制度：至少两人同行
4. 通讯保障：手机电量充足

### 应急方案
1. 提前踩点确认卫生间位置
2. 携带便携应急设备
3. 设定多个安全退出点
4. 保持与同伴的持续沟通`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20outdoor%20park%20and%20nature%20scene%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20organic%20shapes%2C%20clean%20modern%20aesthetic%2C%20no%20text&width=800&height=450&seq=img-outdoor&orientation=landscape',
    readTime: '7',
    publishedAt: '2026-08-04',
  },
  {
    slug: 'breathing-techniques',
    title: '憋尿挑战中的呼吸调节技巧——控制身心的关键',
    category: 'techniques',
    excerpt: '正确的呼吸方式能有效缓解憋尿过程中的不适感。本文介绍多种呼吸调节技巧，帮助你在挑战中保持冷静和舒适。',
    content: `## 呼吸调节技巧

呼吸是连接身心的桥梁，掌握呼吸技巧能显著提升憋尿体验。

### 腹式呼吸法
1. 坐直或平躺，放松全身
2. 用鼻子缓慢吸气4秒，感受腹部隆起
3. 屏住呼吸2秒
4. 用嘴巴缓慢呼气6秒，感受腹部下沉
5. 重复5-10次

### 4-7-8呼吸法
1. 完全呼出肺中空气
2. 用鼻子吸气4秒
3. 屏住呼吸7秒
4. 用嘴巴呼气8秒
5. 重复3-4轮

### 分心呼吸法
将注意力从膀胱区域转移到呼吸节奏上：
1. 在心中默数呼吸次数
2. 想象气息在身体中流动的路径
3. 配合轻柔的音乐节奏呼吸

### 呼吸练习时间建议
- 每日练习10-15分钟
- 挑战前进行5分钟深呼吸热身
- 挑战中每小时进行一次呼吸调节`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20flowing%20breath%20and%20air%20concept%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20curving%20lines%2C%20calm%20serene%20atmosphere%2C%20no%20text&width=800&height=450&seq=img-breathing&orientation=landscape',
    readTime: '6',
    publishedAt: '2026-07-22',
  },
  {
    slug: 'mental-focus-techniques',
    title: '心理专注力训练——憋尿挑战的制胜法宝',
    category: 'techniques',
    excerpt: '憋尿挑战不仅是生理考验，更是心理较量。学习心理专注力训练方法，从冥想、正念到认知重构，全面提升心理承受能力。',
    content: `## 心理专注力训练

心理素质在憋尿挑战中扮演着至关重要的角色。

### 正念冥想训练
1. 选择安静环境，舒适就坐
2. 将注意力集中在呼吸上
3. 当尿意来袭时，客观观察这种感觉
4. 不评判、不对抗，只是觉察
5. 持续练习10-20分钟

### 注意力转移策略
1. **认知任务法**：进行需要高度集中的脑力活动
2. **感官替代法**：听音乐、闻香薰、触摸纹理
3. **想象引导法**：想象自己在广阔的海滩或宁静的森林

### 认知重构技巧
将不适感重新定义：
- 把「尿意」重新理解为「训练信号」
- 把「不适」重新理解为「成长的标志」
- 设定阶段性心理目标

### 压力管理
1. 挑战前进行5分钟渐进式肌肉放松
2. 准备正向肯定语句在心中默念
3. 建立挑战完成后的奖励机制`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20meditation%20and%20mindfulness%20concept%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20flowing%20circles%2C%20calm%20serene%20atmosphere%2C%20no%20text&width=800&height=450&seq=img-mental&orientation=landscape',
    readTime: '8',
    publishedAt: '2026-07-30',
  },
  {
    slug: 'bladder-anatomy-101',
    title: '膀胱健康入门——认识你的膀胱结构与功能',
    category: 'bladder-health',
    excerpt: '全面了解膀胱的解剖结构、生理功能和常见问题，建立科学的膀胱健康知识体系。',
    content: `## 膀胱结构与功能

膀胱是泌尿系统中的核心器官，了解它的结构和功能对于保护膀胱健康至关重要。

### 膀胱的解剖结构
膀胱位于盆腔内，是一个中空的肌性器官：
1. **膀胱壁**：由三层肌肉组成，具有弹性和收缩能力
2. **膀胱颈**：连接尿道，有括约肌控制排尿
3. **膀胱三角区**：输尿管口与尿道内口之间的区域
4. **膀胱容量**：正常成人约400-600ml

### 排尿的生理过程
1. 尿液从肾脏经输尿管进入膀胱
2. 膀胱逐渐充盈，壁内压力升高
3. 达到阈值后，神经信号传递至大脑
4. 大脑发出排尿指令
5. 膀胱收缩，括约肌松弛，尿液排出

### 膀胱健康的日常维护
1. 保持规律饮水（每日1.5-2L）
2. 定时排尿，不长期憋尿
3. 注意个人卫生
4. 避免久坐不动
5. 定期体检`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20anatomical%20illustration%20of%20bladder%20organ%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20organic%20shapes%2C%20clean%20modern%20medical%20aesthetic%2C%20no%20text&width=800&height=450&seq=img-bladder-anatomy&orientation=landscape',
    readTime: '7',
    publishedAt: '2026-07-16',
  },
  {
    slug: 'pelvic-floor-exercises',
    title: '盆底肌锻炼指南——增强膀胱控制力的科学方法',
    category: 'bladder-health',
    excerpt: '盆底肌是控制排尿的关键肌群。本文提供系统的盆底肌锻炼方案，帮助增强膀胱控制能力，改善泌尿健康。',
    content: `## 盆底肌锻炼

盆底肌群的强度和协调性直接影响排尿控制能力。

### 什么是盆底肌
盆底肌是一组支撑盆腔器官的肌肉群，像一张吊床一样托住膀胱、子宫和直肠。强健的盆底肌是良好排尿控制的基础。

### 凯格尔运动
这是最经典的盆底肌锻炼方法：
1. **找到盆底肌**：尝试中断排尿的动作，用到的就是盆底肌
2. **收紧**：收紧盆底肌5秒
3. **放松**：完全放松5秒
4. **重复**：每组10-15次，每日3组

### 进阶训练
1. **快速收缩**：快速收紧-放松，连续20次
2. **电梯训练**：逐步增加收紧力度，分5个等级
3. **耐力训练**：持续收紧15-30秒

### 注意事项
1. 锻炼时正常呼吸，不要憋气
2. 不要使用腹部或臀部肌肉代偿
3. 坚持至少6-8周才能看到明显效果
4. 如有不适请咨询专业医师`,
    imageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20fitness%20and%20exercise%20concept%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20flowing%20organic%20shapes%2C%20clean%20modern%20aesthetic%2C%20no%20text&width=800&height=450&seq=img-pelvic&orientation=landscape',
    readTime: '8',
    publishedAt: '2026-07-24',
  },
];

export const faqItems = [
  {
    question: '憋尿任务挑战安全吗？',
    answer: '在严格遵守规则、了解自身身体状况并设置安全红线的前提下，憋尿任务挑战是相对安全的。但必须强调的是，任何挑战都不能以牺牲健康为代价。我们建议所有参与者首先咨询医生意见，确认自身适合参与此类活动。',
  },
  {
    question: '正常人能憋尿多长时间？',
    answer: '正常成年人膀胱容量约为400-600ml，一般情况下可以憋尿2-4小时。但具体时间因人而异，取决于膀胱容量、饮水量、代谢速度等多种因素。不建议普通人超过6小时不排尿。',
  },
  {
    question: '憋尿会导致尿路感染吗？',
    answer: '长期或频繁憋尿确实会增加尿路感染的风险。尿液在膀胱中滞留时间过长，细菌有更多时间繁殖。如果出现尿痛、尿频、发热等症状，应立即就医。',
  },
  {
    question: '如何判断自己是否适合参与憋尿挑战？',
    answer: '适合参与憋尿挑战的人应具备以下条件：无泌尿系统疾病史、无肾脏疾病、整体健康状况良好、能够准确感知身体信号。如果你有任何健康疑虑，强烈建议先咨询医生。',
  },
  {
    question: '憋尿挑战后需要注意什么？',
    answer: '挑战后应注意：充分补充水分以促进代谢废物排出；观察排尿是否正常；如有不适及时休息；建议挑战后24小时内不再进行高强度训练。如果出现排尿困难、疼痛或血尿等症状，必须立即就医。',
  },
  {
    question: '盆底肌锻炼真的有效吗？',
    answer: '是的，盆底肌锻炼（如凯格尔运动）已被大量临床研究证实对增强膀胱控制力有效。坚持锻炼6-8周通常能看到明显改善。但需要正确的锻炼方法和持之以恒的毅力。',
  },
];

export const aboutInfo = {
  brandName: '憋尿任务百科',
  tagline: '科学认知，安全挑战，健康第一',
  introduction: '憋尿任务百科是国内领先的憋尿挑战知识分享平台，致力于为参与者提供科学、全面、安全的憋尿任务指导。我们汇聚了医学专家、健身教练和经验丰富的挑战者，共同打造最权威的憋尿挑战知识库。',
  mission: '我们的使命是让每一位憋尿挑战参与者都能在充分了解规则、认识风险、掌握技巧的基础上，安全、科学地完成挑战，同时建立正确的膀胱健康观念。',
  history: '平台成立于2024年，从最初的个人经验分享博客发展至今，已服务超过50万挑战爱好者。我们始终坚持以科学为依据、以安全为底线的内容创作原则。',
  values: [
    { title: '科学为本', description: '所有内容均基于医学研究和专家意见，拒绝不科学的偏方和谣言。' },
    { title: '安全第一', description: '任何挑战指导都以安全为首要考量，明确标注风险边界。' },
    { title: '社区互助', description: '建设积极健康的挑战者社区，互相鼓励、互相监督。' },
    { title: '持续更新', description: '紧跟医学研究进展，持续优化和更新平台内容。' },
  ],
  contact: {
    email: 'contact@bie-niao-challenge.cn',
    wechat: 'BieNiaoBaike',
    weibo: '@憋尿任务百科',
    address: '北京市海淀区中关村科技园区',
    phone: '400-888-9999',
  },
};

export const siteInfo = {
  name: '憋尿任务百科',
  title: '憋尿任务挑战全攻略——了解规则与注意事项',
  description: '憋尿任务挑战全攻略，详细介绍憋尿任务的常见场景、挑战规则、身体注意事项及健康边界，帮助参与者在安全前提下完成憋尿任务，避免潜在风险。',
  keywords: '憋尿任务,憋尿任务挑战规则,憋尿任务注意事项,憋尿任务健康风险,憋尿任务场景设定,憋尿挑战技巧,膀胱健康知识',
  url: 'https://www.bie-niao-challenge.cn',
};